import { getBifrostAuthServiceBaseUrl, getBifrostClientId, getBifrostRedirectUri, getGlobalServiceBaseUrl } from "../utils/helpers/bifrost-config";
import parseCookie from "../utils/helpers/parse-cookie";

const PHONE_OTP_METHOD_ID = "PHONE_OTP";
const EMAIL_OTP_METHOD_ID = "EMAIL_OTP";
const BIFROST_PLATFORM = "WEB";
const BIFROST_LOGIN_METHOD = "sso";
const AU_COUNTRY_CODE = "+61";
const PKCE_RANDOM_BYTES = 64;
const BIFROST_SSO_STORAGE_KEY = "bifrost_sso_state";

let currentFlowToken = "";
let currentCodeVerifier = "";
let currentLoginMethodsPayload = null;
let currentLoginMethodsPromise = null;
let currentLoginVerifier = "";

const base64UrlEncode = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });

    return window.btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
};

const sha256 = async (plain) => {
    const encoder = new window.TextEncoder();
    const data = encoder.encode(plain);
    return window.crypto.subtle.digest("SHA-256", data);
};

const generatePkceS256 = async () => {
    const randomBytes = new Uint8Array(PKCE_RANDOM_BYTES);
    window.crypto.getRandomValues(randomBytes);
    const codeVerifier = base64UrlEncode(randomBytes);
    const codeChallenge = base64UrlEncode(await sha256(codeVerifier));

    return { codeVerifier, codeChallenge };
};

const getBifrostDeviceId = () => (
    parseCookie("bifrost_device_id")
    || parseCookie("statsigStableId")
    || parseCookie("UUID")
    || ""
);

const createHeaders = () => {
    const deviceId = getBifrostDeviceId();

    return {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...(deviceId ? { "x-device-id": deviceId } : {}),
        "x-client-id": getBifrostClientId()
    };
};

const createPhoneIdentifier = (mobileNumber, countryCode = AU_COUNTRY_CODE) => {
    const countryDigits = String(countryCode).replace(/\D/g, "");
    let localDigits = String(mobileNumber || "").trim().replace(/\D/g, "");

    if (localDigits.startsWith(countryDigits)) {
        localDigits = localDigits.slice(countryDigits.length);
    }

    if (localDigits.startsWith("0")) {
        localDigits = localDigits.slice(1);
    }

    return `+${countryDigits}${localDigits}`;
};

const getFlowState = () => ({
    deviceId: getBifrostDeviceId(),
    clientId: getBifrostClientId(),
    redirectUri: getBifrostRedirectUri(),
    platform: BIFROST_PLATFORM,
    loginMethod: BIFROST_LOGIN_METHOD
});

const parseJsonResponse = async (response) => {
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw payload;
    }

    return payload;
};

const normalizeTokenResponse = (payload = {}) => {
    const responsePayload = payload?.data || payload;

    return {
        token: responsePayload?.access_token || responsePayload?.token,
        refresh_token: responsePayload?.refresh_token,
        session_id: responsePayload?.session_id,
        expires_in: responsePayload?.expires_in,
        refresh_token_expiry_in: responsePayload?.refresh_token_expiry_in
    };
};

const OTP_METHOD_IDS = ["PHONE_OTP", "EMAIL_OTP"];

const extractLoginVerifier = (payload = {}) => {
    const loginMethodsData = payload?.data || payload || {};
    const methods = loginMethodsData?.methods || loginMethodsData?.login_methods || [];

    if (Array.isArray(methods)) {
        const otpMethod = methods.find((m) => (
            OTP_METHOD_IDS.includes(m?.id) || OTP_METHOD_IDS.includes(m?.method_id)
        ));
        const verifier = otpMethod?.login_verifier || otpMethod?.loginVerifier;
        if (verifier) return verifier;
    }

    return loginMethodsData?.login_verifier || loginMethodsData?.loginVerifier || "";
};

const getLoginMethods = async () => {
    if (currentLoginMethodsPayload) {
        return currentLoginMethodsPayload;
    }

    if (currentLoginMethodsPromise) {
        return currentLoginMethodsPromise;
    }

    const authServiceBaseUrl = getBifrostAuthServiceBaseUrl();
    currentLoginMethodsPromise = fetch(`${authServiceBaseUrl}/oauth2/method/${getBifrostClientId()}`, {
        method: "GET",
        headers: { Accept: "application/json" }
    })
        .then(parseJsonResponse)
        .then((payload) => {
            currentLoginMethodsPayload = payload;
            currentLoginVerifier = extractLoginVerifier(payload);
            return payload;
        })
        .catch((error) => {
            currentLoginMethodsPromise = null;
            throw error;
        });

    return currentLoginMethodsPromise;
};

const startLoginFlow = async () => {
    if (!currentLoginVerifier) {
        await getLoginMethods();
    }

    const authServiceBaseUrl = getBifrostAuthServiceBaseUrl();
    const { codeVerifier, codeChallenge } = await generatePkceS256();
    const query = new window.URLSearchParams({
        response_type: "code",
        client_id: getBifrostClientId(),
        redirect_uri: getBifrostRedirectUri(),
        state: JSON.stringify(getFlowState()),
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        ...(getBifrostDeviceId() ? { device_id: getBifrostDeviceId() } : {}),
        ...(currentLoginVerifier ? { login_verifier: currentLoginVerifier } : {})
    });

    const response = await fetch(`${authServiceBaseUrl}/oauth2/auth?${query.toString()}`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include"
    });

    const payload = await parseJsonResponse(response);
    currentCodeVerifier = codeVerifier;
    currentFlowToken = payload?.data?.flow_token || "";

    return payload;
};

const sendOtp = async (identifier, countryCode = AU_COUNTRY_CODE, options = {}) => {
    const { resendOtp = false, useEmailOtp = false } = typeof options === "object" ? options : {};
    const authServiceBaseUrl = getBifrostAuthServiceBaseUrl();

    if (!resendOtp || !currentFlowToken) {
        await startLoginFlow();
    }

    const methodId = useEmailOtp ? EMAIL_OTP_METHOD_ID : PHONE_OTP_METHOD_ID;
    const formattedIdentifier = useEmailOtp ? identifier : createPhoneIdentifier(identifier, countryCode);

    const response = await fetch(`${authServiceBaseUrl}/oauth2/otp/generate`, {
        method: "POST",
        headers: createHeaders(),
        credentials: "include",
        body: JSON.stringify({
            flow_token: currentFlowToken,
            method_id: methodId,
            identifier: formattedIdentifier,
            ...(resendOtp ? { resend_otp: true } : {})
        })
    });

    return parseJsonResponse(response);
};

// eslint-disable-next-line max-statements
const verifyOtp = async ({ otp, mobileNumber, countryCode = AU_COUNTRY_CODE, metadata = {}, useEmailOtp = false }) => {
    const authServiceBaseUrl = getBifrostAuthServiceBaseUrl();
    const methodId = useEmailOtp ? EMAIL_OTP_METHOD_ID : PHONE_OTP_METHOD_ID;
    const identifier = useEmailOtp ? mobileNumber : createPhoneIdentifier(mobileNumber, countryCode);

    const otpResponse = await fetch(`${authServiceBaseUrl}/oauth2/otp/verify`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-device-id": getBifrostDeviceId(),
            "x-client-id": getBifrostClientId()
        },
        credentials: "include",
        mode: "cors",
        body: JSON.stringify({
            flow_token: currentFlowToken,
            method_id: methodId,
            identifier,
            code: otp,
            ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {})
        })
    });

    if (!otpResponse.ok) {
        const errData = await otpResponse.json().catch(() => ({}));
        throw errData;
    }

    const otpData = await otpResponse.json().catch(() => ({}));
    const authCode = otpData?.code;

    if (!authCode) {
        throw new Error("No auth code received from OTP verification");
    }

    const params = new window.URLSearchParams({
        code: authCode,
        redirect_uri: getBifrostRedirectUri(),
        code_verifier: currentCodeVerifier,
        client_id: getBifrostClientId(),
        device_id: getBifrostDeviceId()
    });

    const tokenResponse = await fetch(`${getGlobalServiceBaseUrl()}/auth/token?${params.toString()}`, {
        method: "GET",
        credentials: "include",
        mode: "cors",
        headers: { "Content-Type": "application/json" }
    });

    const tokenData = await parseJsonResponse(tokenResponse);
    return normalizeTokenResponse(tokenData);
};

const refreshToken = async ({ refreshToken: bifrostRefreshToken, sessionId }) => {
    const authServiceBaseUrl = getBifrostAuthServiceBaseUrl();
    const response = await fetch(`${authServiceBaseUrl}/oauth2/token`, {
        method: "POST",
        headers: createHeaders(),
        credentials: "include",
        body: JSON.stringify({
            grant_type: "refresh_token",
            refresh_token: bifrostRefreshToken,
            client_id: getBifrostClientId(),
            session_id: sessionId,
            redirect_uri: getBifrostRedirectUri()
        })
    });

    const payload = await parseJsonResponse(response);

    return {
        token: payload?.data?.access_token,
        refresh_token: payload?.data?.refresh_token,
        session_id: payload?.data?.session_id,
        expires_in: payload?.data?.expires_in,
        refresh_token_expiry_in: payload?.data?.refresh_token_expiry_in
    };
};

const introspectToken = async ({ token }) => {
    const authServiceBaseUrl = getBifrostAuthServiceBaseUrl();
    const response = await fetch(`${authServiceBaseUrl}/oauth2/introspect`, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        credentials: "include"
    });

    const payload = await parseJsonResponse(response);
    return payload?.data || {};
};

const logout = async ({ accessToken, sessionId }) => {
    const response = await fetch(`${getGlobalServiceBaseUrl()}/auth/logout`, {
        method: "POST",
        headers: createHeaders(),
        credentials: "include",
        body: JSON.stringify({
            access_token: accessToken,
            ...(sessionId ? { session_id: sessionId } : {})
        })
    });

    return parseJsonResponse(response);
};

const getSsoMethodMetadata = async (methodId) => {
    const payload = await getLoginMethods();
    const methods = payload?.data?.methods || payload?.data || [];
    const method = Array.isArray(methods)
        ? methods.find((m) => m?.id === methodId)
        : null;

    return method?.meta_data || {};
};

const SSO_AUTH_URLS = {
    GOOGLE_SSO: "https://accounts.google.com/o/oauth2/v2/auth",
    APPLE_SSO: "https://appleid.apple.com/auth/authorize"
};

const SSO_SCOPES = {
    GOOGLE_SSO: "openid email profile",
    APPLE_SSO: "name email"
};

// eslint-disable-next-line max-statements
const initiateSocialLogin = async (methodId) => {
    const ssoRedirectUri = window.location.origin;
    const authServiceBaseUrl = getBifrostAuthServiceBaseUrl();
    const { codeChallenge } = await generatePkceS256();

    if (!currentLoginVerifier) {
        await getLoginMethods();
    }

    // Start Bifrost auth flow via fetch to get flow_token
    const authQuery = new window.URLSearchParams({
        response_type: "code",
        client_id: getBifrostClientId(),
        redirect_uri: ssoRedirectUri,
        state: JSON.stringify({ deviceId: getBifrostDeviceId(), clientId: getBifrostClientId(), redirectUri: ssoRedirectUri, isSSO: true }),
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
        ...(getBifrostDeviceId() ? { device_id: getBifrostDeviceId() } : {}),
        ...(currentLoginVerifier ? { login_verifier: currentLoginVerifier } : {})
    });

    const authResponse = await fetch(`${authServiceBaseUrl}/oauth2/auth?${authQuery.toString()}`, {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "include"
    });
    const authPayload = await parseJsonResponse(authResponse);
    currentFlowToken = authPayload?.data?.flow_token || "";

    // Get SSO provider metadata (Google/Apple client_id, redirect_uri)
    const metadata = await getSsoMethodMetadata(methodId);
    const ssoClientId = metadata?.client_id;
    const ssoProviderRedirectUri = metadata?.redirect_uri;

    if (!ssoClientId || !ssoProviderRedirectUri) {
        throw new Error("SSO method metadata missing client_id or redirect_uri");
    }

    // Save state for post-redirect token exchange
    window.sessionStorage.setItem(BIFROST_SSO_STORAGE_KEY, JSON.stringify({
        returnUrl: window.location.href,
        isSSO: true
    }));

    // Build Bifrost state (passed through Google/Apple → Bifrost callback)
    const bifrostState = window.btoa(JSON.stringify({
        method_id: methodId,
        flow_token: currentFlowToken,
        device_id: getBifrostDeviceId(),
        country_code: "AU",
        client_id: getBifrostClientId(),
        redirect_uri: ssoRedirectUri,
        client_redirect_uri: ssoRedirectUri
    })).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");

    // Redirect browser to Google/Apple OAuth
    const authUrl = SSO_AUTH_URLS[methodId];
    const scopes = SSO_SCOPES[methodId] || "openid email profile";
    const isApple = methodId === "APPLE_SSO";

    const oauthParams = new window.URLSearchParams({
        redirect_uri: ssoProviderRedirectUri,
        response_type: isApple ? "code id_token" : "code",
        client_id: ssoClientId,
        scope: scopes,
        state: bifrostState,
        ...(isApple ? { response_mode: "form_post" } : { prompt: "consent" })
    });

    window.location.href = `${authUrl}?${oauthParams.toString()}`;
};

const exchangeCodeForTokens = async (code) => {
    window.sessionStorage.removeItem(BIFROST_SSO_STORAGE_KEY);

    const authServiceBaseUrl = getBifrostAuthServiceBaseUrl();
    const response = await fetch(`${authServiceBaseUrl}/oauth2/token`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            ...(getBifrostDeviceId() ? { "x-device-id": getBifrostDeviceId() } : {})
        },
        credentials: "include",
        body: JSON.stringify({
            grant_type: "authorization_code",
            code,
            redirect_uri: window.location.origin,
            client_id: getBifrostClientId(),
            session_info: {
                platform: BIFROST_PLATFORM,
                loginMethod: BIFROST_LOGIN_METHOD
            }
        })
    });

    const payload = await parseJsonResponse(response);
    return normalizeTokenResponse(payload);
};

const getSsoReturnUrl = () => {
    const raw = window.sessionStorage.getItem(BIFROST_SSO_STORAGE_KEY);
    const ssoState = raw ? JSON.parse(raw) : {};
    return ssoState.returnUrl || "/";
};

const isSsoCallback = () => {
    if (typeof window === "undefined") return false;
    const params = new window.URLSearchParams(window.location.search);
    if (!params.get("code")) return false;

    // Check state param for isSSO flag
    try {
        const state = JSON.parse(params.get("state") || "{}");
        if (state.isSSO) return true;
    } catch (e) {
        // state not valid JSON
    }

    // Fallback: check sessionStorage
    const raw = window.sessionStorage.getItem(BIFROST_SSO_STORAGE_KEY);
    if (raw) {
        try {
            const ssoState = JSON.parse(raw);
            return ssoState.isSSO === true;
        } catch (e) {
            // ignore
        }
    }

    return false;
};

const getSsoCodeFromUrl = () => {
    const params = new window.URLSearchParams(window.location.search);
    return params.get("code") || "";
};

const cleanSsoUrlParams = () => {
    const url = new window.URL(window.location.href);
    url.searchParams.delete("code");
    url.searchParams.delete("scope");
    url.searchParams.delete("state");
    window.history.replaceState({}, "", url.pathname + url.search);
};

export default {
    getLoginMethods,
    sendOtp,
    verifyOtp,
    startLoginFlow,
    refreshToken,
    introspectToken,
    logout,
    initiateSocialLogin,
    exchangeCodeForTokens,
    getSsoReturnUrl,
    isSsoCallback,
    getSsoCodeFromUrl,
    cleanSsoUrlParams
};
